import { useTranslation } from 'next-i18next';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Rating } from '@hotelplan/components.common.rating';
import { BsLink } from '@hotelplan/core.basis.bs-link';
import { getCurrentOpeningHours } from '@hotelplan/fdr.lib.misc.schedule';
import { useTransfer } from '@hotelplan/fdr.lib.router.push';
import { useOnClickOutside } from '@hotelplan/libs.hooks-dom';
import { FdrAgencyOpenStatus } from '@hotelplan/supergraph-api';
import { getLocationLink } from 'components/domain/fdr-agency-detail';
import { FdrAgencyFragment } from 'schemas/fragment/agency/fdr-agency.generated';
import {
  CloseBtn,
  DetailIcon,
  DetailIconBlock,
  DetailSectionTitle,
  FdrAgencyAddress,
  FdrAgencyMapOpeningStatus,
  FdrAgencyRating,
  FdrAgencySchedule,
  FdrAgencyTitle,
  InfoIconsWrapper,
  InfoSectionHeader,
  InfoSectionWrapper,
  RatingIcon,
} from './fdr-agency-map.styles';

export interface IFdrAgencyDetailedInfoProps {
  agency: FdrAgencyFragment;
  close?(): void;
}

export function FdrAgencyDetailedInfo({
  agency,
  close,
}: IFdrAgencyDetailedInfoProps): ReactElement {
  const [t] = useTranslation('agency');
  const { transfer } = useTransfer();

  const [openedTill, setOpenedTill] = useState<string | null>(undefined);
  const windowRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(windowRef, close);

  const {
    name,
    address: { zip, city, street },
    coordinates,
    openingHours,
    openStatus,
    review,
  } = agency;

  const agencyMapLink = coordinates ? getLocationLink(coordinates) : undefined;

  useEffect(() => {
    const shifts = openingHours
      ? getCurrentOpeningHours(openingHours, new Date())?.shifts
      : [];

    setOpenedTill(shifts?.length ? shifts[shifts.length - 1].to : null);
  }, [openingHours]);

  const { averageRating, totalReviewCount } = review || {};
  const opened = openStatus !== FdrAgencyOpenStatus.Close;

  return (
    <InfoSectionWrapper
      onClick={() =>
        agency?.webMeta.link
          ? transfer(agency?.webMeta.link, false, true)
          : undefined
      }
      ref={windowRef}
    >
      <InfoSectionHeader>
        <DetailSectionTitle>
          <FdrAgencyTitle>{name}</FdrAgencyTitle>
          <FdrAgencyTitle className="agency-city">{city}</FdrAgencyTitle>
        </DetailSectionTitle>
        <FdrDetailedInfoIcons agencyMapLink={agencyMapLink} />
        <CloseBtn
          name="close"
          onClick={e => {
            e.stopPropagation();
            close();
          }}
        />
      </InfoSectionHeader>
      {averageRating && totalReviewCount ? (
        <FdrAgencyRating>
          <span className="rating average">{averageRating?.toFixed(1)}</span>
          <Rating
            rating={averageRating}
            maxRating={5}
            ratingContent={<RatingIcon name={'star-full'} />}
            halfWayRatingContent={<RatingIcon name={'star-half'} />}
            remainRatingContent={<RatingIcon name={'star-blank'} />}
            className="rating stars"
          />
          <span className="rating count">{`(${totalReviewCount})`}</span>
        </FdrAgencyRating>
      ) : null}
      <FdrAgencyAddress>
        {street ? <span className="address">{street}</span> : null}
        {zip ? <span className="address">{zip}</span> : null}
      </FdrAgencyAddress>
      <FdrAgencySchedule>
        {openStatus !== null && (
          <FdrAgencyMapOpeningStatus opened={opened} showStatusIcon={false} />
        )}
        {opened && openedTill ? (
          <span className="opened-till">
            {t('agency:openingHours.willClose', { time: openedTill })}
          </span>
        ) : null}
      </FdrAgencySchedule>
    </InfoSectionWrapper>
  );
}

const ROUTE_ICON = 'route-icon';
const ROUTE_ICON_HOVER = 'route-icon-hover';
const WEB_ICON = 'web-icon';
const WEB_ICON_HOVER = 'web-icon-hover';

interface IFdrDetailedInfoIconsProps {
  agencyMapLink: string;
}

function FdrDetailedInfoIcons({
  agencyMapLink,
}: IFdrDetailedInfoIconsProps): ReactElement {
  const [t] = useTranslation('agency');

  const [routeIcon, setRouteIcon] = useState<string>(ROUTE_ICON);
  const [webIcon, setWebIcon] = useState<string>(WEB_ICON);

  return (
    <InfoIconsWrapper>
      <BsLink href={agencyMapLink} target={'__blank'}>
        <DetailIconBlock>
          <DetailIcon
            onMouseEnter={() => setRouteIcon(ROUTE_ICON_HOVER)}
            onMouseLeave={() => setRouteIcon(ROUTE_ICON)}
            name={routeIcon}
          />
          <span className="icon-text">{t('agency:route.icon')}</span>
        </DetailIconBlock>
      </BsLink>
      <DetailIconBlock className="web-icon">
        <DetailIcon
          onMouseEnter={() => setWebIcon(WEB_ICON_HOVER)}
          onMouseLeave={() => setWebIcon(WEB_ICON)}
          name={webIcon}
        />
        <span className="icon-text">{t('agency:web.icon')}</span>
      </DetailIconBlock>
    </InfoIconsWrapper>
  );
}
